import { refreshViaAuthService } from "../app/modules/Auth/_redux/authCrud";
import * as auth from "../app/modules/Auth/_redux/authRedux";

/** Renova o access token antes de expirar (margem em ms). */
const ACCESS_TOKEN_REFRESH_BUFFER_MS = 60_000;

/** Renovacao descartada porque a sessao mudou embaixo dela. */
export const SESSION_CHANGED = "SESSION_CHANGED";

/**
 * A unica sessao Hydra ativa no browser (cookie HttpOnly do auth-service),
 * entao no maximo uma renovacao fica em voo por vez, sem chave por sessao.
 */
let pendingRefresh = null;

function normalizeExpiresIn(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function refreshOnce() {
  if (pendingRefresh) {
    return pendingRefresh;
  }

  pendingRefresh = refreshViaAuthService().finally(() => {
    pendingRefresh = null;
  });

  return pendingRefresh;
}

async function completeAuthServiceRefresh(store) {
  const { authToken } = store.getState().auth;
  const data = await refreshOnce();
  if (!data?.access_token) {
    throw new Error("refresh missing access_token");
  }
  const state = store.getState().auth;
  if (state.authToken !== authToken) {
    throw new Error(SESSION_CHANGED);
  }
  const nextRefresh = data.refresh_token || state.refreshToken;
  const nextIdToken =
    typeof data.id_token === "string" && data.id_token ? data.id_token : state.idToken;
  const expiresIn = normalizeExpiresIn(data.expires_in);
  store.dispatch(
    auth.actions.refreshTokens(data.access_token, nextRefresh, expiresIn, nextIdToken)
  );
  return data;
}

/**
 * O refresh-token real agora vive so num cookie HttpOnly (nunca chega ao
 * JS), entao refreshToken no Redux fica sempre undefined para sessoes do
 * fluxo novo. idToken e' o sinal de elegibilidade: so sessoes Hydra o tem
 * (login legado por senha nunca popula idToken).
 */
async function runTokenRefreshIfExpired(store, config) {
  if (config.skipAuthRefresh) {
    return;
  }
  const url = String(config.url || "");
  if (url.includes("/oauth2/token")) {
    return;
  }

  const { authToken, idToken, accessTokenExpiresAt } = store.getState().auth;
  if (!idToken || !authToken || !accessTokenExpiresAt) {
    return;
  }
  if (Date.now() < accessTokenExpiresAt - ACCESS_TOKEN_REFRESH_BUFFER_MS) {
    return;
  }

  await completeAuthServiceRefresh(store);
}

export default function setupAxios(axios, store) {
  axios.defaults.withCredentials = true;

  axios.interceptors.request.use(
    async config => {
      try {
        await runTokenRefreshIfExpired(store, config);
      } catch (error) {
        if (error?.message !== SESSION_CHANGED) {
          store.dispatch(auth.actions.logout());
        }
        return Promise.reject(new Error("SESSION_REFRESH_FAILED"));
      }

      const {
        auth: { authToken }
      } = store.getState();

      if (authToken && !config.skipAuthRefresh) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      return config;
    },
    err => Promise.reject(err)
  );

  axios.interceptors.response.use(
    res => res,
    async error => {
      const original = error.config;
      if (!original || original.skipAuthRefresh) {
        return Promise.reject(error);
      }
      if (error.response?.status !== 401) {
        return Promise.reject(error);
      }
      if (original._retryAuthRefresh) {
        return Promise.reject(error);
      }

      const { idToken, authToken } = store.getState().auth;
      if (!idToken || !authToken) {
        return Promise.reject(error);
      }

      original._retryAuthRefresh = true;

      try {
        const data = await completeAuthServiceRefresh(store);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return axios(original);
      } catch (refreshError) {
        if (refreshError?.message !== SESSION_CHANGED) {
          store.dispatch(auth.actions.logout());
        }
        return Promise.reject(error);
      }
    }
  );
}
